<template>
    <b-modal id="schema-input-detail-modal"
             :title="modalTitle"
             hide-footer
             size="xl"
             modal-class="default-modal"
             @hide="closeModal"
    >
        <div class="action-btns">
            <template v-if="mode === 'readOnly'">
                <div style="height: 32px"/>
            </template>
            <template v-else-if="mode === 'detail'">
                <b-button variant="outline-secondary"
                          class="mr-2"
                          @click="copyFromDetail"
                >
                    {{ $t('복사하여 추가') }}
                </b-button>
                <b-button variant="outline-primary"
                          @click="changeMode('edit')"
                >
                    {{ $t('수정') }}
                </b-button>
            </template>
            <template v-else-if="mode === 'add'">
                <b-button v-if="allowCopyImport"
                          variant="outline-secondary"
                          class="mr-2"
                          @click="$emit('request-copy-import')"
                >
                    {{ $t('다른 스키마에서 복사') }}
                </b-button>
                <b-button variant="outline-primary"
                          form="schema-input-form"
                          type="submit"
                >
                    {{ $t('저장') }}
                </b-button>
            </template>
            <template v-else-if="mode === 'edit'">
                <b-button variant="outline-danger"
                          @click="confirmDelete"
                >
                    {{ $t('삭제') }}
                </b-button>
                <b-button variant="outline-primary"
                          form="schema-input-form"
                          type="submit"
                >
                    {{ $t('저장') }}
                </b-button>
                <b-button variant="outline-secondary"
                          @click="changeMode('detail')"
                >
                    {{ $t('취소') }}
                </b-button>
            </template>
        </div>
        <div class="tabs-container">
            <b-tabs v-model="tabIndex" class="default-tabs" :class="{'with-actions': mode !== 'add'}" content-class="mt-3">
                <b-tab  :title="$t('기본 정보')">
                    <template v-if="loading">
                        <b-skeleton-img no-aspect class="empty-content"></b-skeleton-img>
                    </template>
                    <template v-else>
                        <schema-input-detail v-if="mode === 'detail' || mode === 'readOnly'" :detail-data="detailData"
                                             :projects="projects"
                                             :allowed-values="allowedValues"
                        />
                        <template v-else>
                            <b-alert v-if="isCopy && mode === 'add'" show variant="warning" class="mb-3 py-2">
                                <strong>{{ copySourceLabel }}</strong> 에서 복사됨 —
                                <template v-if="lockProjectToSelected">현재 프로젝트에 등록됩니다. 저장을 누르면 반영됩니다.</template>
                                <template v-else>연결 프로젝트를 선택하면 저장할 수 있습니다.</template>
                            </b-alert>
                            <schema-input-form ref="schemaInputForm"
                                               :key="`${mode}-${isCopy ? 'copy' : (detailId == null ? 'new' : detailId)}`"
                                               :mode="mode"
                                               :detail-data="isCopy ? copySeed : detailData"
                                               :projects="projects"
                                               :prop-allowed-values="isCopy ? copyAllowedValues : allowedValues"
                                               :propSelectedProjectCodeId="selectedProjectCodeId"
                                               :is-copy="isCopy"
                                               :lock-project-to-selected="lockProjectToSelected"
                                               @updateDetailData="updateDetailData"
                                               @refreshAllowedValues="refreshAllowedValuesHandler"
                            />
                        </template>
                    </template>
                </b-tab>
                <b-tab v-if="mode === 'detail' || mode === 'readOnly'" 
                       :title="$t('입력 값 검증')">
                    <template v-if="loading">
                        <b-skeleton-img no-aspect class="empty-content"></b-skeleton-img>
                    </template>
                    <template v-else>
                        <schema-input-validate  :detail-data="detailData"
                                               :projects="projects"
                                               :allowed-values="allowedValues"
                        />
                    </template>
                </b-tab>
            </b-tabs>
        </div>
    </b-modal>
</template>

<script>
import {deleteInputKey, getInputKey, listAllowedValues} from '@shared_utils/api/projectSchema';

import SchemaInputDetail
    from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/SchemaInputDetail.vue';
import SchemaInputForm from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/SchemaInputForm.vue';
import SchemaInputValidate
    from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/SchemaInputValidate.vue';
import {getTargetProjectText} from '@shared/components/admin/pages/Management/common-data';

export default {
    name: 'SchemaInputModal',
    components: {
        SchemaInputValidate,
        SchemaInputForm,
        SchemaInputDetail,
    },
    props: {
        detailId: {
            type: [Number, String, null],
            default: null,
        },
        projects: {
            type: [Array, null],
            default: null,
        },
        selectedProjectCodeId: {
            type: [Number, String, null],
            default: null,
        },
        // add 모드에서 "다른 스키마에서 복사" 버튼 노출 여부. 배정 콘솔에서만 true.
        // 버튼 클릭 시 request-copy-import 이벤트만 emit 하고, 실제 목록/복사는
        // 부모가 picker 모달로 처리한 뒤 openAsCopy(id) 를 호출한다.
        allowCopyImport: {
            type: Boolean,
            default: false,
        },
        // 연결 프로젝트를 selectedProjectCodeId 로 고정+잠금(복사 포함). 콘솔에서만 true.
        lockProjectToSelected: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            mode: 'detail', // detail | add | edit | readOnly
            tabIndex: 1,
            detailData: {id: null},
            loading: false,
            allowedValues: [],
            isCopy: false,
            copySeed: null,
            copyAllowedValues: [],
            copySourceLabel: null,
        };
    },
    computed: {
        modalTitle() {
            if (this.mode === 'add') {
                return this.isCopy ? this.$t('스키마 입력 값 복사 등록') : this.$t('스키마 입력 값 등록');
            } else if (this.mode === 'edit') {
                return this.$t('스키마 입력 값 수정');
            } else {
                return this.$t('스키마 입력 값 상세');
            }
        },
        readOnly() {
            return this.mode === 'readOnly';
        }
    },
    watch: {
        detailId: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.fetchDetailData();
                } else {
                    this.detailData = {id: null};
                }
            }
        }
    },
    methods: {
        changeMode(mode) {
            this.mode = mode;
            this.tabIndex = 1;
        },
        buildCopySeedFrom(src, allowedValues) {
            this.copySeed = {
                key_name: src.key_name,
                value_type: src.value_type,
                label_key: src.label_key,
                label_texts: src.label_texts,
                is_required: src.is_required,
                description: src.description,
            };
            // 배너용 소스 라벨: 키 이름 + 프로젝트 정보 조합
            const projectText = getTargetProjectText(src.project_id, this.projects);
            this.copySourceLabel = projectText ? `${src.key_name} (${projectText})` : src.key_name;
            this.copyAllowedValues = (allowedValues || []).map((v, i) => ({
                __uid: `copy-${i}-${v.label_key || v.actual_value || 'av'}-${Date.now()}`,
                actual_value: v.actual_value,
                label_key: v.label_key,
                label_texts: v.label_texts || {},
            }));
        },
        // 상세 모드: 이미 로드된 detailData/allowedValues 재사용 (재조회 없음)
        copyFromDetail() {
            this.buildCopySeedFrom(this.detailData, this.allowedValues);
            this.isCopy = true;
            this.changeMode('add'); // 폼 fresh mount (key: add-copy)
        },
        // 목록 진입: 소스 id로 직접 조회 후 시드 (조회 완료 후 mode 전환 → fresh mount 보장)
        async openAsCopy(sourceId) {
            try {
                this.loading = true;
                this.tabIndex = 1;
                const res = await getInputKey(sourceId);
                const src = res.data;
                let av = [];
                if (src.value_type === 'list') {
                    const avRes = await listAllowedValues({
                        params: {input_key_id: sourceId, active_only: true}
                    });
                    const {allowed_values} = avRes.data || {};
                    av = Array.isArray(allowed_values)
                        ? allowed_values.slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                        : [];
                }
                this.buildCopySeedFrom(src, av);
                this.isCopy = true;
                this.mode = 'add';
            } catch (e) {
                console.error(e);
                this.$EventBus.$emit('toast-failure', this.$t('스키마 입력 값 복사'), this.$t('복사할 스키마 입력 값을 불러오는 중 오류가 발생했습니다. 개발 팀에 문의해주세요.'));
                this.closeModal();
            } finally {
                this.loading = false;
            }
        },
        closeModal() {
            this.mode = 'detail';
            this.isCopy = false;
            this.copySeed = null;
            this.copyAllowedValues = [];
            this.copySourceLabel = null;
            this.$bvModal.hide('schema-input-detail-modal');
        },
        async fetchDetailData() {
            try {
                this.loading = true;
                this.detailData = {id: null};
                this.allowedValues = [];
                const res = await getInputKey(this.detailId);
                this.detailData = res.data;
                if (this.detailData.value_type === 'list') {
                    await this.fetchAllowedValues();
                }
            } catch (e) {
                console.error(e);
                console.dir(e);
                this.$EventBus.$emit('toast-failure', this.$t('스키마 입력 값 상세'), this.$t('스키마 입력 값 정보를 불러오는 중 오류가 발생했습니다. 개발 팀에 문의해주세요.'));
            } finally {
                this.loading = false;
            }
        },
        async fetchAllowedValues() {
            try {
                const res = await listAllowedValues({
                    params: {
                        input_key_id: this.detailData.id,
                        active_only: true
                    }
                });
                const {allowed_values} = res.data || {};
                if (Array.isArray(allowed_values)) {
                    const mapped = allowed_values.map((v, i) => {
                        const texts = v.label_texts || {};
                        return {
                            __uid: `${v.label_key || v.actual_value || 'av'}-${i}-${Date.now()}`,
                            id: v.id,
                            actual_value: v.actual_value,
                            label_key: v.label_key,
                            label_texts: texts,
                            label_texts_ko: texts.ko ?? '',
                            label_texts_en: texts.en ?? '',
                            label_texts_ca: texts.ca ?? '',
                            sort_order: v.sort_order,
                        };
                    });
                    mapped.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
                    this.allowedValues = mapped;
                } else {
                    this.allowedValues = [];
                }
            } catch (e) {
                this.allowedValues = [];
            }
        },
        async refreshAllowedValuesHandler(done) {
            try {
                await this.fetchAllowedValues();
                if (this.$refs.schemaInputForm && typeof this.$refs.schemaInputForm.reloadFromProps === 'function') {
                    // 갱신된 allowedValues 를 명시적으로 전달.
                    // propAllowedValues 의 prop 반영은 다음 tick 에 일어나므로 stale 일 수 있다.
                    this.$refs.schemaInputForm.reloadFromProps(this.allowedValues);
                }
            } finally {
                if (typeof done === 'function') {
                    done();
                }
            }
        },
        async updateDetailData(detailData) {
            if (this.mode === 'add') {
                this.$emit('addData', detailData);
                this.closeModal();
            } else if (this.mode === 'edit') {
                this.$emit('updateDetailData', detailData);
                this.detailData = detailData;
                await this.fetchAllowedValues();
                this.changeMode('detail');
            }
        },
        confirmDelete() {
            this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: this.$t('스키마 입력 값 삭제'),
                message: this.$t('현 스키마 입력 값을 삭제하시겠습니까?')
            })
                .then((v) => {
                    if (v) {
                        this.deleteCondition();
                    }
                });
        },
        deleteCondition() {
            deleteInputKey(this.detailData.id)
                .then(() => {
                    this.$EventBus.$emit('toast-success', this.$t('스키마 입력 값 삭제'), this.$t('스키마 입력 값가 성공적으로 삭제되었습니다.'));
                    this.$emit('deleteData', this.detailData.id);
                    this.closeModal();
                })
                .catch((error) => {
                    console.error(error);
                    this.$EventBus.$emit('toast-failure', this.$t('스키마 입력 값 삭제'), this.$t('스키마 입력 값 삭제 중 오류가 발생했습니다. 개발 팀에 문의해주세요.'));
                });
        }
    },
};
</script>

<style scoped lang="scss">

</style>
